/* ---------- Core utils ---------- */

export function clone(obj) {
  return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

export function normalizeLangCode(s) {
  if (!s || typeof s !== "string") return "";
  return s.trim().toLowerCase();
}

export function deriveLanguagesFromDictionaries(dictionaries) {
  const dict = (dictionaries || []).find((d) => d?.type === "formDictionary");
  const phrases = dict?.phrases;
  if (!phrases || typeof phrases !== "object" || Array.isArray(phrases)) return [];
  return Object.keys(phrases).map(normalizeLangCode).filter(Boolean);
}

export function mapPhrasesByLanguage(dictionaries = [], requestedType = "formDictionary") {
  const dict = dictionaries.find((d) => d?.type === requestedType) || {};
  const phrases = dict.phrases || {};
  const out = {};
  Object.keys(phrases).forEach((lng) => {
    out[normalizeLangCode(lng)] = phrases[lng] || {};
  });
  return out;
}

export function resolvePhrase(phrasesByLang, lang, key) {
  if (!key) return "";
  const map = phrasesByLang?.[normalizeLangCode(lang)] || {};
  return map[key] ?? "";
}

export function setPhrase(form, lang, key, value, type = "formDictionary") {
  const next = clone(form);
  const idx = next.dictionaries.findIndex((d) => d?.type === type);
  const lng = normalizeLangCode(lang);
  if (idx === -1) {
    next.dictionaries.push({ type, phrases: { [lng]: { [key]: value } } });
    return next;
  }
  const phrases = next.dictionaries[idx].phrases || {};
  const langMap = { ...(phrases[lng] || {}), [key]: value };
  next.dictionaries[idx].phrases = { ...phrases, [lng]: langMap };
  return next;
}

export function updateControl(form, pageIndex, controlIndex, mutator) {
  const next = clone(form);
  const page = next.pages?.[pageIndex];
  if (!page || !Array.isArray(page.controls)) return next;
  const ctrl = page.controls?.[controlIndex];
  if (!ctrl) return next;
  mutator(ctrl);
  return next;
}

/* ---------- Small utilities ---------- */

export function inBounds(i, len) { return Number.isInteger(i) && i >= 0 && i < len; }
export function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

export function uniquePageTitle(pages, base) {
  const existing = new Set((pages || []).map(p => (p?.title || "").toLowerCase()));
  if (!existing.has(base.toLowerCase())) return base;
  let i = 2;
  while (existing.has(`${base} ${i}`.toLowerCase())) i += 1;
  return `${base} ${i}`;
}

export function uniqueControlKey(controls, base) {
  const existing = new Set((controls || []).map(c => String(c?.key || "").toLowerCase()));
  if (!existing.has(base.toLowerCase())) return base;
  let i = 2;
  while (existing.has(`${base}_${i}`.toLowerCase())) i += 1;
  return `${base}_${i}`;
}

/** Remap index when reordering within the same list by removing at `from`
 *  and inserting at `hover` (post-removal coordinate). */
export function remapIndexSameList(idx, from, hover) {
  if (!Number.isInteger(idx) || from === hover) return idx;
  if (idx === from) return hover;

  if (from < hover) {
    // moving down: (from, hover] shift left by 1
    if (idx > from && idx <= hover) return idx - 1;
    return idx;
  } else {
    // moving up: [hover, from) shift right by 1 (includes hover)
    if (idx >= hover && idx < from) return idx + 1;
    return idx;
  }
}

/* ---------- Options (legacy + dictionary-backed) ---------- */

/**
 * Get a select-options set by key from dictionaries (type === "selectOptions").
 * Supports two shapes:
 *  A) language-bucketed: phrases[lang][setKey] = { value: label, ... }
 *  B) flat:              phrases[setKey]        = { value: label, ... }
 * Returns a plain object map { value: label } or null if not found.
 */
export function getSelectOptionsMap(dictionaries = [], setKey, lang = "en") {
  if (!setKey) return null;

  const selectDicts = (dictionaries || []).filter((d) => d?.type === "selectOptions");
  if (!selectDicts.length) return null;

  // 1) language-bucketed: phrases[lang]?.[setKey]
  for (const d of selectDicts) {
    const phrases = d?.phrases;
    if (phrases && typeof phrases === "object" && !Array.isArray(phrases)) {
      const byLang = phrases[normalizeLangCode(lang)];
      if (byLang && typeof byLang === "object" && !Array.isArray(byLang)) {
        const map = byLang[setKey];
        if (map && typeof map === "object" && !Array.isArray(map)) {
          return { ...map };
        }
      }
    }
  }

  // 2) fallback to any other language that has the set
  for (const d of selectDicts) {
    const phrases = d?.phrases;
    if (phrases && typeof phrases === "object" && !Array.isArray(phrases)) {
      const langKeys = Object.keys(phrases);
      for (const lk of langKeys) {
        const candidate = phrases[lk]?.[setKey];
        if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
          return { ...candidate };
        }
      }
    }
  }

  // 3) flat: phrases[setKey] = { value: label }
  for (const d of selectDicts) {
    const phrases = d?.phrases;
    if (phrases && typeof phrases === "object" && !Array.isArray(phrases)) {
      const flat = phrases[setKey];
      if (flat && typeof flat === "object" && !Array.isArray(flat)) {
        return { ...flat };
      }
    }
  }

  return null;
}

/** Normalize { value: label } map to [{ value, label }] sorted by label. */
export function normalizeOptionsFromMap(mapObj) {
  if (!mapObj || typeof mapObj !== "object") return [];
  return Object.entries(mapObj)
    .map(([value, label]) => ({ value, label: String(label ?? "") }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * Legacy inline options -> normalized array. Legacy `name` is a DICT KEY.
 * We resolve via phrasesByLang[lang][name] when present, else fall back to `name` itself.
 */
export function normalizeLegacyOptionsArray(optionsArr = [], phrasesByLang = {}, lang = "en") {
  const pMap = phrasesByLang?.[normalizeLangCode(lang)] || {};
  return (Array.isArray(optionsArr) ? optionsArr : []).map((opt) => {
    const value = opt?.value;
    const keyOrText = String(opt?.name ?? "");
    const resolved = (keyOrText && pMap && Object.prototype.hasOwnProperty.call(pMap, keyOrText))
      ? pMap[keyOrText]
      : keyOrText; // allow raw HTML/text if not a dict key
    return { value, label: String(resolved ?? "") };
  });
}

/** Return array form from dictionaries if present; otherwise derive from map. */
export function getSelectOptionsArray(dictionaries = [], setKey, lang = "en") {
  if (!setKey) return [];

  const selectDicts = (dictionaries || []).filter((d) => d?.type === "selectOptions");
  if (!selectDicts.length) return [];

  // 1) Flat array at phrases[setKey]
  for (const d of selectDicts) {
    const phrases = d?.phrases;
    const arr = phrases?.[setKey];
    if (Array.isArray(arr)) {
      return arr.map(o => ({
        name: String(o?.name ?? ""),
        value: String(o?.value ?? "")
      }));
    }
  }

  // 2) Language bucketed array at phrases[lang][setKey]
  const lng = normalizeLangCode(lang);
  for (const d of selectDicts) {
    const byLang = d?.phrases?.[lng];
    const arr = byLang?.[setKey];
    if (Array.isArray(arr)) {
      return arr.map(o => ({
        name: String(o?.name ?? ""),
        value: String(o?.value ?? "")
      }));
    }
  }

  // 3) Map fallback -> convert to array
  const map = getSelectOptionsMap(dictionaries, setKey, lang) || {};
  return Object.entries(map).map(([value, label]) => ({
    name: String(label ?? ""),
    value: String(value ?? "")
  }));
}

/**
 * Resolve options for a control supporting BOTH:
 *  - New: control.options is a STRING referencing a set in selectOptions dictionaries
 *  - Legacy: control.options is an ARRAY of { name, value }
 * Returns [{ value, label }]
 */
export function resolveControlOptions(control, dictionaries = [], phrasesByLang = {}, lang = "en") {
  if (!control) return [];
  console.log('[DICT] resolveControlOptions.in', {
  from: 'resolveControlOptions',
  controlOptionsType: Array.isArray(control?.options) ? 'array' : typeof control?.options,
  controlOptionsSample: typeof control?.options === 'string' ? control.options : (control?.options || []).slice(0,3),
  lang
});

  // New system: string reference to set in selectOptions dictionaries
  if (typeof control.options === "string") {
    // Prefer the array (canonical), else map fallback
    const arr = getSelectOptionsArray(dictionaries, control.options, lang);
    if (arr.length) {
      console.log('[DICT] resolveControlOptions.dictArray', { setKey: control.options, outSample: arr.slice(0,3) });

      return arr.map(o => ({ value: o.value, label: o.name }));
    }

    console.log('[DICT] resolveControlOptions.dictMap', {
  setKey: control.options,
  outSample: (map ? Object.entries(map).slice(0,3) : [])
});
    const map = getSelectOptionsMap(dictionaries, control.options, lang);
    console.log('[DICT] resolveControlOptions.legacy', { outSample: (control?.options || []).slice(0,3) });
    return normalizeOptionsFromMap(map);
  }

  // Legacy system: inline array of { name, value }
  if (Array.isArray(control.options)) {
    return normalizeLegacyOptionsArray(control.options, phrasesByLang, lang);
  }

  return [];
}

/* ---------- Control types ---------- */

/** Union of known control types + any types present in pages. */
export function buildControlTypeOptions(pages = [], known = []) {
  const found = new Set(
    (pages || [])
      .flatMap((p) => (p?.controls || []))
      .map((c) => (c?.type ? String(c.type).toUpperCase() : ""))
      .filter(Boolean)
  );
  return Array.from(new Set([...(known || []).map((t) => String(t).toUpperCase()), ...found]));
}

/* ---- Normalization for editor ---- */

export function safeParseObject(maybeString) {
  if (maybeString && typeof maybeString === "string") {
    try {
      const parsed = JSON.parse(maybeString);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    } catch {}
  }
  return null;
}

/** Best-effort JSON parser for values that might be JSON. */
export function tryParseJSON(value) {
  if (typeof value !== "string") return value;
  const s = value.trim();
  if (!s) return value;

  const looksJson = /^[\[{]/.test(s) || /^(true|false|null|-?\d+(\.\d+)?([eE][+-]?\d+)?)$/.test(s);
  if (!looksJson) return value;

  try {
    return JSON.parse(s);
  } catch {
    return value;
  }
}

/** Normalize a control so editor sees objects/arrays, not stringified JSON. */
export function normalizeControlForEditor(ctrl = {}) {
  const out = { ...ctrl };

  // Common JSON-ish fields on controls
  out.attributes = tryParseJSON(out.attributes) || (typeof out.attributes === "object" ? out.attributes : {});
  out.action = tryParseJSON(out.action);
  out.policies = tryParseJSON(out.policies);
  out.flags = tryParseJSON(out.flags);
  out.header = tryParseJSON(out.header);
  out.footer = tryParseJSON(out.footer);

  // required can be stored as "true"/"false"
  if (typeof out.required === "string") {
    out.required = out.required.toLowerCase() === "true";
  } else {
    out.required = !!out.required;
  }

  // Normalize options
  out.options = normalizeOptionsForEditor(out.options);

  // Type as UPPERCASE (defensive)
  if (out.type) out.type = String(out.type).toUpperCase();

  return out;
}

/** Normalize page-level JSON-ish fields (header/footer/navigation) and controls. */
export function normalizePageForEditor(page = {}) {
  const out = { ...page };

  out.header = tryParseJSON(out.header);
  out.footer = tryParseJSON(out.footer);

  // navigation can be an array or a stringified array
  const nav = tryParseJSON(out.navigation);
  out.navigation = Array.isArray(nav) ? nav : Array.isArray(out.navigation) ? out.navigation : [];

  // controls
  const controls = Array.isArray(out.controls) ? out.controls : tryParseJSON(out.controls);
  out.controls = Array.isArray(controls) ? controls.map(normalizeControlForEditor) : [];

  return out;
}

/** Normalize options so each entry is an object, not a stringified object. */
export function normalizeOptionsForEditor(options) {
  // New system: options can be a string key (leave as-is, unless it's actually a JSON array string)
  if (typeof options === "string") {
    const maybe = tryParseJSON(options);
    if (Array.isArray(maybe)) {
      return maybe.map((item) => (typeof item === "string" ? tryParseJSON(item) : item));
    }
    return options;
  }

  // Legacy: array — entries themselves might be stringified JSON
  if (Array.isArray(options)) {
    return options.map((item) => (typeof item === "string" ? tryParseJSON(item) : item));
    }

  // Anything else -> leave alone / null
  return options;
}

/** Entry point used by the editor — call this on initial load and before committing saves. */
export function normalizePagesForEditor(pages) {
  const src = Array.isArray(pages) ? pages : tryParseJSON(pages);
  const arr = Array.isArray(src) ? src : [];
  return arr.map(normalizePageForEditor);
}

/**
 * Update a selectOptions set by key on the first dictionary with type === "selectOptions".
 * nextOptions must be an array of { name, value } (label in `name`, machine code in `value`).
 */
export function updateSelectOptionsSet(dicts = [], setKey, nextOptions = []) {
  if (!setKey) return dicts;
  const out = Array.isArray(dicts) ? dicts.map(d => ({ ...d })) : [];
  const idx = out.findIndex(d => d && d.type === "selectOptions");
  if (idx === -1) return out;

  const phrases = out[idx]?.phrases && typeof out[idx].phrases === "object"
    ? { ...out[idx].phrases }
    : {};

  phrases[setKey] = Array.isArray(nextOptions)
    ? nextOptions.map(o => ({
        name: String(o?.name ?? ""),
        value: String(o?.value ?? "")
      }))
    : [];

  out[idx] = { ...out[idx], phrases };
  return out;
}
